import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import checkchallanImage from "../../assets/challan-online.png";
import styles from "./CarInsurance.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faUpload } from "@fortawesome/free-solid-svg-icons";

function CarInsurance() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    vehicleNumber: "",
    phoneNumber: "",
    email: "",
    pincode: "",
  });
  const [files, setFiles] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value.toUpperCase() });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    if (!formData.vehicleNumber) {
      setError("Please enter your vehicle number");
      return false;
    }
    if (!formData.phoneNumber) {
      setError("Please enter your mobile number");
      return false;
    }
    return true;
  };

  const handleInitialSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowDocUpload(true);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const token = localStorage.getItem("token");
    const vehicle_id = localStorage.getItem("vehicle_id");

    if (!token) {
      setError("Authentication token missing. Please log in again.");
      return;
    }
    if (!vehicle_id) {
      setError("Vehicle ID missing. Please select a vehicle.");
      return;
    }

    setLoading(true);

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    const form = new FormData();
    form.append("service_type", "Insurance");
    form.append("vehicle_id", vehicle_id);
    form.append(
      "additional_info",
      JSON.stringify({
        name: formData.name,
        phone_number: formData.phoneNumber,
        email: formData.email,
        pincode: formData.pincode,
        carInsurance: true,
      })
    );

    if (files.length > 0) {
      files.forEach((file) => {
        form.append("documents", file);
      });
    }

    try {
      const response = await fetch(
        "https://api.rtoxpert.com/api/service-requests",
        {
          method: "POST",
          headers,
          body: form,
          redirect: "follow",
        }
      );

      if (response.ok) {
        setShowPopup(true);
        setError(null);
        setTimeout(() => {
          setShowPopup(false);
          navigate("/profile");
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Connection error. Please check your internet connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        {/* Left Content */}
        <div className={styles.leftContent}>
          <h1 className={styles.title}>Renew Your Insurance Instantly</h1>

          {!showDocUpload ? (
            /* Initial Form */
            <div className={styles.formContainer}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Full Name"
                  className={styles.input}
                  value={formData.name}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="vehicleNumber"
                  placeholder="Enter Vehicle Number"
                  className={styles.input}
                  value={formData.vehicleNumber}
                  onChange={handleChange}
                  required
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="Enter Mobile Number"
                  className={styles.input}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  maxLength="10"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Enter Email Address (Optional)"
                  className={styles.input}
                  value={formData.email}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="pincode"
                  placeholder="Enter PIN Code (Optional)"
                  className={styles.input}
                  value={formData.pincode}
                  onChange={handleChange}
                  maxLength="6"
                />
                <button
                  className={styles.button}
                  onClick={handleInitialSubmit}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Get Insurance"}
                </button>
                <p className={styles.terms}>
                  By continuing, I agree to the{" "}
                  <a href="#" className={styles.link}>
                    Terms of Use
                  </a>{" "}
                  &{" "}
                  <a href="#" className={styles.link}>
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          ) : (
            /* Document Upload Section */
            <div className={styles.docUploadContainer}>
              <h3 className={styles.uploadTitle}>Upload Required Documents</h3>

              <div className={styles.documentsList}>
                <div className={styles.documentsNeeded}>
                  <h4>Documents Required:</h4>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>Vehicle Registration Certificate (RC)</p>
                  </div>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>Previous Insurance Policy (if applicable)</p>
                  </div>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>ID Proof (Aadhar/PAN Card)</p>
                  </div>
                </div>

                <div className={styles.fileUploadArea}>
                  <div className={styles.fileInputGroup}>
                    <div className={styles.fileUploadWrapper}>
                      <label className={styles.fileUploadLabel}>
                        <FontAwesomeIcon
                          icon={faUpload}
                          className={styles.uploadIcon}
                        />
                        Choose File(s)
                        <input
                          type="file"
                          className={styles.fileInput}
                          onChange={handleFileChange}
                          multiple
                        />
                      </label>
                    </div>

                    {files.length > 0 && (
                      <ul className={styles.uploadedFiles}>
                        {files.map((file, index) => (
                          <li key={index} className={styles.fileItem}>
                            <span>📄 {file.name}</span>
                            <button
                              className={styles.removeFileBtn}
                              onClick={() => removeFile(index)}
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className={styles.buttonGroup}>
                    <button
                      className={styles.backButton}
                      onClick={() => setShowDocUpload(false)}
                    >
                      Back
                    </button>
                    <button
                      className={styles.submitButton}
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!showDocUpload && (
            <div>
              <p className={styles.newCarText}>
                Bought a new Car?{" "}
                <a href="#" className={styles.insureLink}>
                  Insure now
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Right Image */}
        <div className={styles.rightContent}>
          <div className={styles.blueCircle}></div>
          <img
            src={checkchallanImage}
            alt="Traffic officer checking vehicle with driver using smartphone app"
            className={styles.image}
          />
        </div>
      </div>

      {error && <p className={styles.errorText}>{error}</p>}

      {showPopup && (
        <div className={styles.popup}>
          <p>
            Insurance request submitted successfully! Agent will reachout to you
            shortly. Redirecting to profile...
          </p>
        </div>
      )}
    </div>
  );
}

export default CarInsurance;